import React, { createContext, useContext, useState, useCallback } from 'react';
import NotificationToast from '../components/NotificationToast';
import StackedNotificationToast from '../components/NotificationToast/StackedNotificationToast';
import {
  Notification,
  NotificationContextType,
  NotificationDto,
  GroupedNotificationWrapper
} from '../types/notification';

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [groupedNotifications, setGroupedNotifications] = useState<GroupedNotificationWrapper[]>([]);
  const [isAnyGroupHovered, setAnyGroupHovered] = useState(false);

  const showNotification = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now() + Math.floor(Math.random() * 10000);
    setNotifications(prev => [...prev, { id, message, type }]);
  }, []);

  const showGroupedNotification = useCallback((notification: NotificationDto, allNotifications: NotificationDto[]) => {
    setGroupedNotifications(prev => {
      const existingIndex = prev.findIndex(item => item.notification.entity_type === notification.entity_type);
      const newItem = { notification, allNotifications };
      
      if (existingIndex >= 0) {
        // Replace existing notification of same entity_type
        const newNotifications = [...prev];
        newNotifications[existingIndex] = newItem;
        return newNotifications;
      } else {
        // Add new notification
        return [...prev, newItem];
      }
    });
  }, []);

  const removeNotification = useCallback((id: number) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const removeGroupedNotification = useCallback((entityType: string) => {
    setGroupedNotifications(prev => prev.filter(item => item.notification.entity_type !== entityType));
  }, []);

  return (
    <NotificationContext.Provider value={{ 
      showNotification, 
      showGroupedNotification,
      notifications, 
      groupedNotifications,
      removeNotification,
      removeGroupedNotification,
      isAnyGroupHovered,
      setAnyGroupHovered
    }}>
      {children}
      {/* Regular notifications */}
      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 z-[9999] space-y-2 max-h-[calc(100vh-2rem)]">
          {notifications.map((notification, idx) => (
            <NotificationToast
              key={notification.id}
              message={notification.message}
              type={notification.type}
              duration={3500}
              onClose={() => removeNotification(notification.id)}
              index={idx}
            />
          ))}
        </div>
      )}
      {/* Grouped notifications */}
      {groupedNotifications.length > 0 && (
        <div className="fixed top-4 right-4 z-[9999] space-y-4 max-h-[calc(100vh-2rem)] flex flex-col items-end">
          {groupedNotifications.map((item, idx) => (
            <StackedNotificationToast
              key={item.notification.entity_type}
              notification={item.notification}
              allNotifications={item.allNotifications}
              onClose={() => removeGroupedNotification(item.notification.entity_type)}
              index={idx}
            />
          ))}
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};